import {Component} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatProgressBar} from '@angular/material/progress-bar';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {finalize} from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService } from '../../services/dialog.service';
import { ApiUserServices } from '../../services/user.service';
import { displayErrorMessage, displaySuccessMessage } from '../../shared/utils/utils';
@Component({
    selector: 'app-change-password-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressBar,
        ReactiveFormsModule,
        TranslateModule
    ],
    templateUrl: './change-password-dialog.component.html',
    styleUrls: ['./change-password-dialog.component.scss']
})
export class ChangePasswordDialogComponent {
    changePasswordForm: FormGroup;
    loading = false;
    hideOldPassword = true;
    hideNewPassword = true;
    hideConfirmPassword = true;

    constructor(
        private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
        private fb: FormBuilder,
        private dialogService: DialogService,
        private translate: TranslateService,
        private userService: ApiUserServices
    ) {
        this.changePasswordForm = this.fb.group({
            oldPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(100)]],
            newPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(100), this.passwordPatternValidator]],
            confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(100)]]
        }, {validators: this.passwordMatchValidator});

        // Listen to newPassword changes and clear confirmPassword mismatch error if they match
        this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(() => {
            const newPassword = this.changePasswordForm.get('newPassword')?.value;
            const confirmPassword = this.changePasswordForm.get('confirmPassword')?.value;
            const confirmPasswordControl = this.changePasswordForm.get('confirmPassword');

            if (confirmPasswordControl && newPassword && confirmPassword) {
                if (newPassword === confirmPassword) {
                    // Clear passwordMismatch error if passwords match
                    if (confirmPasswordControl.hasError('passwordMismatch')) {
                        const errors = {...confirmPasswordControl.errors};
                        delete errors['passwordMismatch'];
                        const hasErrors = Object.keys(errors).length > 0;
                        confirmPasswordControl.setErrors(hasErrors ? errors : null, {emitEvent: false});
                    }
                } else {
                    // Re-validate if passwords don't match
                    this.changePasswordForm.updateValueAndValidity({emitEvent: false});
                }
            }
        });
    }

    passwordPatternValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value) {
            return null;
        }

        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumeric = /[0-9]/.test(value);
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);

        if (!hasUpperCase || !hasLowerCase || !hasNumeric || !hasSpecialChar) {
            return {passwordPattern: true};
        }

        return null;
    }

    passwordMatchValidator(form: FormGroup) {
        const newPassword = form.get('newPassword');
        const confirmPassword = form.get('confirmPassword');

        if (!newPassword || !confirmPassword || !newPassword.value || !confirmPassword.value) {
            return null;
        }

        if (newPassword.value !== confirmPassword.value) {
            confirmPassword.setErrors({passwordMismatch: true});
            return {passwordMismatch: true};
        }

        return null;
    }

    get oldPasswordControl() {
        return this.changePasswordForm.get('oldPassword');
    }

    get newPasswordControl() {
        return this.changePasswordForm.get('newPassword');
    }

    get confirmPasswordControl() {
        return this.changePasswordForm.get('confirmPassword');
    }

    onSubmit(): void {
        this.changePasswordForm.markAllAsTouched();
        if (this.changePasswordForm.invalid) {
            return;
        }
        this.loading = true;
        const {oldPassword, newPassword, confirmPassword} = this.changePasswordForm.value;

        this.userService.changePassword({
            oldPassword,
            newPassword,
            confirmNewPassword: confirmPassword
        }).pipe(finalize(() => (this.loading = false))).subscribe({
            next: () => {
                this.dialogRef.close(true);
                displaySuccessMessage(this.translate.instant('auth.changePasswordSuccess'), this.dialogService);
            },
            error: (err) => {
                displayErrorMessage(err, this.dialogService, this.translate.instant('auth.changePasswordFailed'));
            }
        });
    }

    onCancel(): void {
        if (this.loading) {
            return;
        }
        this.dialogRef.close();
    }

    toggleOldPasswordVisibility(): void {
        this.hideOldPassword = !this.hideOldPassword;
    }

    toggleNewPasswordVisibility(): void {
        this.hideNewPassword = !this.hideNewPassword;
    }

    toggleConfirmPasswordVisibility(): void {
        this.hideConfirmPassword = !this.hideConfirmPassword;
    }
}

